/**
 * Web Briefing helper. The Briefing works fully standalone (deterministic
 * `buildBriefing` over the live incident + insights); when the API is reachable
 * and AI is enabled it can additionally return an AI-rephrased read under the
 * grounding contract. Either way the facts come only from the structured
 * context — the AI never adds anything.
 */
import { buildBriefing, type Incident, type Insight } from '@duty/shared';

export interface BriefingResult {
  text: string;
  aiUsed: boolean;
}

/** Deterministic, client-side briefing (no network, no AI). */
export function buildBriefingLocal(incident: Incident, insights: readonly Insight[]): BriefingResult {
  return { text: buildBriefing(incident, insights), aiUsed: false };
}

/** Try the API (AI phrasing) first, falling back to the local deterministic briefing. */
export async function fetchBriefing(
  incident: Incident,
  insights: readonly Insight[],
  signal?: AbortSignal,
): Promise<BriefingResult> {
  try {
    const res = await fetch('/api/briefing', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ incident, insights }),
      signal: signal ?? AbortSignal.timeout(8000),
    });
    if (res.ok) {
      const data = (await res.json()) as { briefing?: string; aiUsed?: boolean };
      if (typeof data.briefing === 'string' && data.briefing.trim()) {
        return { text: data.briefing, aiUsed: Boolean(data.aiUsed) };
      }
    }
  } catch {
    /* API unavailable — use the deterministic local briefing */
  }
  return buildBriefingLocal(incident, insights);
}
